/**
 * BookNotePage - 作品（本）ごとの読書ノート。本文は TextDocument(entity_type='product_note') に保存する
 *
 * - 「/」→ Highlight でこの本のハイライトを初めに出して引用できる（すべての本にも切り替えられる）
 * - 本文が空のときは、要約・印象に残った箇所・問い・自分の考えのテンプレートを入れられる
 */

import type { Editor, JSONContent } from '@tiptap/react'
import { BookOpen, Highlighter, History, LayoutTemplate } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'

import { BOOK_NOTE_ENTITY_TYPE, type BookNoteInfo, getBookNoteInfo } from '@/api/services/book-note'
import type { TextDocumentRevision } from '@/api/services/memo'
import { BacklinksPanel } from '@/components/memo/backlinks-panel'
import { BookHighlightsPanel } from '@/components/memo/book-highlights-panel'
import { LocalGraphPanel } from '@/components/memo/local-graph-panel'
import { MemoHistorySheet } from '@/components/memo/memo-history-sheet'
import { MemoRevisionPreview } from '@/components/memo/memo-revision-preview'
import { RelatedPanel } from '@/components/memo/related-panel'
import { Header } from '@/components/shared/header'
import { NotionEditor } from '@/components/shared/notion-editor'
import { useHighlightPickerProduct } from '@/components/tiptap-node/highlight-ref-node'
import { Button } from '@/components/ui/button'
import { useGoBack } from '@/hooks/useGoBack'
import { apiErrorMessage } from '@/lib/api-error'
import { clearNotionEditorBackup } from '@/lib/notion-editor-backup'
import { PATHS } from '@/routes/paths'

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

const STATUS_LABEL: Record<SaveStatus, string> = {
  idle: '',
  saving: '保存中…',
  saved: '保存済み',
  error: '保存失敗',
}

const TEMPLATE_SECTIONS = ['要約', '印象に残った箇所', '問い・疑問', '自分の考え・やってみること']

const TEMPLATE: JSONContent = {
  type: 'doc',
  content: TEMPLATE_SECTIONS.flatMap((title) => [
    { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: title }] },
    { type: 'paragraph' },
  ]),
}

export const BookNotePage = () => {
  const { product_id } = useParams<{ product_id: string }>()
  const productId = Number(product_id)
  const isValidId = Number.isInteger(productId) && productId > 0
  const goBack = useGoBack(PATHS.MEMO.LIST)

  const [info, setInfo] = useState<BookNoteInfo | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [status, setStatus] = useState<SaveStatus>('idle')
  const [editor, setEditor] = useState<Editor | null>(null)
  const [isEmpty, setIsEmpty] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [previewRevision, setPreviewRevision] = useState<TextDocumentRevision | null>(null)
  const [editorReloadKey, setEditorReloadKey] = useState(0)

  useHighlightPickerProduct(isValidId ? productId : null)

  useEffect(() => {
    if (!isValidId) return
    let cancelled = false
    setInfo(null)
    setLoadError(null)
    getBookNoteInfo(productId)
      .then((res) => {
        if (!cancelled) setInfo(res)
      })
      .catch((err) => {
        if (!cancelled) setLoadError(apiErrorMessage(err, '作品を読み込めませんでした'))
      })
    return () => {
      cancelled = true
    }
  }, [isValidId, productId])

  useEffect(() => {
    if (!editor) return
    const update = () => setIsEmpty(editor.isEmpty)
    update()
    editor.on('update', update)
    return () => {
      editor.off('update', update)
    }
  }, [editor])

  const handleRestored = () => {
    clearNotionEditorBackup(BOOK_NOTE_ENTITY_TYPE, productId)
    setPreviewRevision(null)
    setHistoryOpen(false)
    setEditor(null)
    setEditorReloadKey((k) => k + 1)
  }

  const insertHighlight = () => editor?.chain().focus('end').insertHighlightRef().run()
  const insertTemplate = () => editor?.chain().setContent(TEMPLATE).focus('start').run()

  const errorMessage = !isValidId ? '作品が見つかりません' : loadError

  return (
    <div className="flex h-dvh flex-col">
      <Header
        title={
          <div className="flex w-full min-w-0 items-center gap-2">
            <BookOpen className="size-4 shrink-0 text-muted-foreground" />
            <div className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="truncate text-base font-semibold">{info?.title ?? '読書ノート'}</span>
              {info && (
                <span className="text-[11px] text-muted-foreground">
                  読書ノート · ハイライト {info.highlight_count} 件
                </span>
              )}
            </div>
            <span className="w-14 shrink-0 text-right text-xs text-muted-foreground">{STATUS_LABEL[status]}</span>
            <button
              type="button"
              onClick={insertHighlight}
              disabled={!editor || !!previewRevision}
              aria-label="ハイライトを引用"
              title="ハイライトを引用"
              className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40"
            >
              <Highlighter className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              disabled={!info}
              aria-label="履歴"
              title="履歴"
              className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <History className="size-4" />
            </button>
          </div>
        }
        onClose={goBack}
      />

      <div className="flex-1 overflow-x-hidden overflow-y-auto">
        <div className={`w-full max-w-full py-4 sm:px-8 ${historyOpen ? 'pb-[50dvh]' : 'pb-24'}`}>
          {errorMessage && <p className="py-10 text-center text-sm text-muted-foreground">{errorMessage}</p>}
          {info && previewRevision && (
            <MemoRevisionPreview
              entityType={BOOK_NOTE_ENTITY_TYPE}
              entityId={productId}
              revision={previewRevision}
              onClose={() => setPreviewRevision(null)}
              onRestored={handleRestored}
            />
          )}
          {info && !previewRevision && (
            <>
              {info.highlight_count > 0 && (
                <BookHighlightsPanel productId={productId} count={info.highlight_count} editor={editor} />
              )}
              {editor && isEmpty && (
                <div className="mx-6 mb-2 flex flex-wrap items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground sm:mx-0">
                  <span className="min-w-0 flex-1">見出しを入れて書き始めることもできます</span>
                  <Button size="sm" variant="outline" onClick={insertTemplate}>
                    <LayoutTemplate className="size-4" />
                    テンプレートを入れる
                  </Button>
                </div>
              )}
              <NotionEditor
                key={editorReloadKey}
                entityType={BOOK_NOTE_ENTITY_TYPE}
                entityId={productId}
                placeholder="この本の考察を書く…「/」→ Highlight で引用、「[[」でメモや本にリンク"
                onStatusChange={setStatus}
                onEditorReady={setEditor}
              />
              <BacklinksPanel targetType="product" targetId={productId} />
              <RelatedPanel kind="product_note" refId={productId} editor={editor} />
              <LocalGraphPanel centerId={`book:${productId}`} />
            </>
          )}
        </div>
      </div>

      {info && (
        <MemoHistorySheet
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          entityType={BOOK_NOTE_ENTITY_TYPE}
          entityId={productId}
          selectedId={previewRevision?.id ?? null}
          onSelect={setPreviewRevision}
        />
      )}
    </div>
  )
}
